import { DOCUMENT, Injectable, effect, inject, signal } from '@angular/core';

export type GisgoTheme = 'light' | 'dark';

const STORAGE_KEY = 'gisgo-ops-theme';

/**
 * Thème light/dark piloté par `data-theme` sur `<html>`.
 *
 * ⚠️ L'attribut doit être posé sur `documentElement`, jamais sur `<body>` :
 * les tokens dérivés (ex. `--ops-sidebar-bg: var(--surface)`) sont résolus
 * sur `:root` — posé plus bas, le dark hérite des valeurs light déjà calculées.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);

  readonly theme = signal<GisgoTheme>(this.restore());

  constructor() {
    effect(() => {
      const theme = this.theme();
      this.document.documentElement.dataset['theme'] = theme;
      try {
        this.document.defaultView?.localStorage.setItem(STORAGE_KEY, theme);
      } catch {
        // stockage indisponible (navigation privée) : le thème reste en mémoire
      }
    });
  }

  toggle(): void {
    this.theme.update((t) => (t === 'light' ? 'dark' : 'light'));
  }

  set(theme: GisgoTheme): void {
    this.theme.set(theme);
  }

  private restore(): GisgoTheme {
    // ?theme=dark|light prime (vérification headless, parité design-preview)
    const fromUrl = new URLSearchParams(this.document.defaultView?.location.search ?? '').get(
      'theme',
    );
    if (fromUrl === 'dark' || fromUrl === 'light') {
      return fromUrl;
    }
    try {
      const stored = this.document.defaultView?.localStorage.getItem(STORAGE_KEY);
      return stored === 'dark' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  }
}
